import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import axios from 'axios';
import { Calendar, MapPin, Users, Banknote, ArrowLeft, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState({
    name: '',
    email: '',
    phone: '',
    participants: 1,
    participant_names: ['']
  });
  const [submitting, setSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchEvent();
  }, [id]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/events/${id}`);
      setEvent(response.data);
    } catch (error) {
      toast.error('Événement non trouvé');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  // Update participant names array when participants count changes
  const handleParticipantsChange = (count) => {
    const newCount = Math.max(1, parseInt(count) || 1);
    const currentNames = booking.participant_names || [];
    let newNames;
    
    if (newCount > currentNames.length) {
      // Add empty slots
      newNames = [...currentNames, ...Array(newCount - currentNames.length).fill('')];
    } else {
      // Remove excess slots
      newNames = currentNames.slice(0, newCount);
    }
    
    setBooking({ 
      ...booking, 
      participants: newCount,
      participant_names: newNames
    });
  };

  const handleParticipantNameChange = (index, value) => {
    const newNames = [...booking.participant_names];
    newNames[index] = value;
    setBooking({ ...booking, participant_names: newNames });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/bookings`, {
        event_id: id,
        name: booking.name,
        email: booking.email,
        phone: booking.phone,
        participants: booking.participants,
        participant_names: booking.participant_names.filter(name => name.trim() !== '')
      });
      setBookingSuccess(true);
      toast.success('Réservation confirmée ! Vous recevrez un email de confirmation.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réservation');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#8B4B7C] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!event) return null;

  const spotsLeft = event.max_participants - event.current_participants;
  const isFull = spotsLeft <= 0;
  const totalPrice = event.price * booking.participants;

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Header />
      
      <main className="pt-28 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back button */}
          <button
            onClick={() => navigate('/events')}
            className="flex items-center text-[#6B5A62] hover:text-[#8B4B7C] mb-8 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Retour aux événements
          </button>

          <div className="grid lg:grid-cols-3 gap-12">
            {/* Event Details */}
            <div className="lg:col-span-2 space-y-8">
              {/* Image */}
              {event.image_url && (
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={event.image_url}
                    alt={event.title}
                    className="w-full h-[400px] object-cover"
                  />
                </div>
              )}

              {/* Content */}
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <span className={`px-4 py-1.5 rounded-full text-sm font-medium ${
                    event.type === 'workshop'
                      ? 'bg-[#5A8F6E] text-white'
                      : 'bg-[#8B4B7C] text-white'
                  }`}>
                    {event.type === 'workshop' ? 'Workshop' : 'Événement'}
                  </span>
                  {isFull && (
                    <span className="px-4 py-1.5 rounded-full text-sm font-medium bg-red-500 text-white">
                      Complet
                    </span>
                  )}
                </div>

                <h1 className="font-heading text-4xl text-[#3D2E35]">{event.title}</h1>
                
                <div className="flex flex-wrap gap-6 mt-6 text-[#6B5A62]">
                  <div className="flex items-center">
                    <Calendar size={20} className="mr-2 text-[#C45B9F]" />
                    <span>{formatDate(event.date)}</span>
                  </div>
                  <div className="flex items-center">
                    <Clock size={20} className="mr-2 text-[#C45B9F]" />
                    <span>{formatTime(event.date)}</span>
                  </div>
                  <div className="flex items-center">
                    <MapPin size={20} className="mr-2 text-[#C45B9F]" />
                    <span>{event.location}</span>
                  </div>
                </div>

                <div className="mt-8 prose prose-lg max-w-none">
                  <p className="text-[#6B5A62] leading-relaxed whitespace-pre-wrap">{event.description}</p>
                </div>
              </div>
            </div>

            {/* Booking Form */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl p-6 shadow-lg sticky top-28">
                {bookingSuccess ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-[#5A8F6E] rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="font-heading text-xl text-[#3D2E35] mb-2">Réservation confirmée !</h3>
                    <p className="text-[#6B5A62]">
                      Un email de confirmation vous a été envoyé à {booking.email}
                    </p>
                    <Button
                      onClick={() => navigate('/events')}
                      className="mt-6 btn-tevaite text-white rounded-xl"
                    >
                      Voir d'autres événements
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-[#6B5A62]">Prix par personne</span>
                        <span className="font-heading text-2xl text-[#8B4B7C]">
                          {event.price.toLocaleString()} XPF
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[#6B5A62]">Places disponibles</span>
                        <span className={`font-medium ${spotsLeft <= 3 ? 'text-red-500' : 'text-[#5A8F6E]'}`}>
                          {spotsLeft} places
                        </span>
                      </div>
                    </div>

                    {isFull ? (
                      <div className="text-center py-6 bg-[#FAF8F5] rounded-xl">
                        <p className="text-[#6B5A62] font-medium">Cet événement est complet</p>
                        <p className="text-sm text-[#6B5A62] mt-2">
                          Contactez-nous pour être informé des prochaines dates
                        </p>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Nom complet *</Label>
                          <Input
                            value={booking.name}
                            onChange={(e) => setBooking({ ...booking, name: e.target.value })}
                            required
                            className="border-[#E8A5C4]/30"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Email *</Label>
                          <Input
                            type="email"
                            value={booking.email}
                            onChange={(e) => setBooking({ ...booking, email: e.target.value })}
                            required
                            className="border-[#E8A5C4]/30"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Téléphone *</Label>
                          <Input
                            type="tel"
                            value={booking.phone}
                            onChange={(e) => setBooking({ ...booking, phone: e.target.value })}
                            required
                            className="border-[#E8A5C4]/30"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Nombre de participants *</Label>
                          <Input
                            type="number"
                            min="1"
                            max={spotsLeft}
                            value={booking.participants}
                            onChange={(e) => handleParticipantsChange(e.target.value)}
                            required
                            className="border-[#E8A5C4]/30"
                          />
                        </div>

                        {/* Participant Names */}
                        {booking.participants > 0 && (
                          <div className="space-y-3">
                            <Label className="flex items-center gap-2">
                              <Users size={16} className="text-[#C45B9F]" />
                              Nom des participants
                            </Label>
                            <div className="space-y-2">
                              {booking.participant_names.map((name, index) => (
                                <Input
                                  key={index}
                                  placeholder={`Participant ${index + 1}`}
                                  value={name}
                                  onChange={(e) => handleParticipantNameChange(index, e.target.value)}
                                  className="border-[#E8A5C4]/30"
                                />
                              ))}
                            </div>
                            <p className="text-xs text-[#6B5A62]">
                              💡 Entrez le nom de chaque participant pour faciliter l'accueil
                            </p>
                          </div>
                        )}

                        {/* Total */}
                        <div className="pt-4 border-t border-[#E8A5C4]/20">
                          <div className="flex items-center justify-between mb-4">
                            <span className="font-medium text-[#3D2E35]">Total</span>
                            <span className="font-heading text-2xl text-[#8B4B7C]">
                              {totalPrice.toLocaleString()} XPF
                            </span>
                          </div>
                        </div>

                        <Button
                          type="submit"
                          disabled={submitting}
                          className="w-full btn-tevaite py-6 rounded-xl text-white font-medium"
                        >
                          {submitting ? (
                            <span className="flex items-center justify-center">
                              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Réservation en cours...
                            </span>
                          ) : (
                            'Réserver maintenant'
                          )}
                        </Button>

                        <p className="text-xs text-center text-[#6B5A62]">
                          Le paiement sera effectué sur place ou par virement
                        </p>
                      </form>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default EventDetailPage;
